// import { CreateUserInput, LoginInput, UserResponse } from '../../models/core/user';
// import { CreateStudentInput } from '../../models/core/student';
// import { CreateSchoolInput } from '../../models/core/school';
// import { create } from 'zustand';

type StorageKey = 'users' | 'currentUser' | 'students' | 'schools' | 'loginAttempts'
                | 'goals' | 'achievements' | 'academicResults' | 'intervalAssessments'
                | 'journeyRecords' | 'notes' | 'aiChats' | 'portfolio';

export const StorageService = {
  // Base methods stay synchronous
  read: (key: StorageKey) => JSON.parse(localStorage.getItem(key) || '[]'),
  find: (key: StorageKey) => localStorage.getItem(key),
  write: (key: StorageKey, value: any) => localStorage.setItem(key, JSON.stringify(value)),
  delete: (key: StorageKey) => localStorage.removeItem(key),

  async get(db: StorageKey, identifier: string): Promise<any> {
    const items = this.read(db);
    return items.find((item: any) => item._id === identifier || item.email === identifier) || null;
  },

  async create(db: StorageKey, item: any) {
    const items = this.read(db);
    const newItem = {
      ...item,
      _id: `${db}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    items.push(newItem);
    this.write(db, items);
    
    return newItem;
  },

  async update(db: StorageKey, identifier: string, updates: Partial<any>) {
    const items = this.read(db);
    const index = items.findIndex((u: any) => u._id === identifier || u.email === identifier);
    
    if (index === -1) return null;

    items[index] = {
      ...items[index],
      ...updates,
      updatedAt: new Date(),
    };

    this.write(db, items);
    return items[index];
  },

  async search(db: StorageKey, query: Partial<any>): Promise<any[]> {
    const items = this.read(db);
    return items.filter((item: any) => {
      return Object.entries(query).every(([key, value]) => item[key] === value);
    });
  },

  // ============ USER MANAGEMENT ============
  
  async getUsers(): Promise<any[]> {
    return this.read('users');
  },

  async getUserByEmail(email: string): Promise<any | null> {
    return this.get('users', email);
  },

  async getUserById(id: string): Promise<any | null> {
    return this.get('users', id);
  },

  async createUser(userData: any): Promise<any> {
    const newUser = {
      ...userData,
      isActive: true,
      lastLogin: null,
    };
    return this.create('users', newUser);
  },

  async updateUser(id: string, updates: Partial<any>): Promise<any | null> {
    const updatedUser = await this.update('users', id, updates);
    
    // Update current user if it's the same user
    const currentUser = await this.getCurrentUser();
    if (currentUser && currentUser._id === id && updatedUser) {
      await this.setCurrentUser(updatedUser);
    }
    
    return updatedUser;
  },

  // ============ STUDENT MANAGEMENT ============
  
  async getStudents(): Promise<any[]> {
    return this.read('students');
  },

  async getStudentById(id: string): Promise<any | null> {
    return this.get('students', id);
  },

  async getStudentByUserId(userId: string): Promise<any | null> {
    const students = await this.getStudents();
    return students.find(student => student.userId === userId) || null;
  },

  async createStudent(studentData: any): Promise<any> {
    return this.create('students', studentData);
  },

  async updateStudent(id: string, updates: Partial<any>): Promise<any | null> {
    return this.update('students', id, updates);
  },

  // ============ SCHOOL MANAGEMENT ============
  
  async getSchools(): Promise<any[]> {
    return this.read('schools');
  },

  async getSchoolById(id: string): Promise<any | null> {
    return this.get('schools', id);
  },

  async getSchoolByUserId(userId: string): Promise<any | null> {
    const schools = await this.getSchools();
    return schools.find(school => school.userId === userId) || null;
  },

  async createSchool(schoolData: any): Promise<any> {
    return this.create('schools', schoolData);
  },

  async updateSchool(id: string, updates: Partial<any>): Promise<any | null> {
    return this.update('schools', id, updates);
  },

  // ============ AUTHENTICATION ============
  
  async authenticateUser(email: string, password: string): Promise<any> {
    const user = await this.getUserByEmail(email);
    
    if (!user) {
      throw new Error('Invalid email or password');
    }
    
    // Check if account is locked
    if (await this.isAccountLocked(email)) {
      throw new Error('Account temporarily locked. Please try again later.');
    }
    
    // Simple password check (in real app, use hashing)
    if (user.password !== password) {
      await this.incrementLoginAttempts(email);
      throw new Error('Invalid email or password');
    }
    
    // Reset login attempts on success
    await this.resetLoginAttempts(email);
    
    // Update last login
    const updatedUser = await this.updateUser(user._id, { lastLogin: new Date() });
    return updatedUser;
  },

  async isAccountLocked(email: string): Promise<boolean> {
    const attempts = await this.getLoginAttempts(email);
    return attempts >= 5; // Lock after 5 failed attempts
  },

  async getLoginAttempts(email: string): Promise<number> {
    const attemptsData = this.find('loginAttempts');
    const attempts = attemptsData ? JSON.parse(attemptsData) : {};
    return attempts[email] || 0;
  },

  async incrementLoginAttempts(email: string): Promise<void> {
    const attemptsData = this.find('loginAttempts');
    const attempts = attemptsData ? JSON.parse(attemptsData) : {};
    attempts[email] = (attempts[email] || 0) + 1;
    localStorage.setItem('loginAttempts', JSON.stringify(attempts));
  },

  async resetLoginAttempts(email: string): Promise<void> {
    const attemptsData = this.find('loginAttempts');
    const attempts = attemptsData ? JSON.parse(attemptsData) : {};
    delete attempts[email];
    localStorage.setItem('loginAttempts', JSON.stringify(attempts));
  },

  // ============ SESSION MANAGEMENT ============
  
  async setCurrentUser(user: any): Promise<void> {
    this.write('currentUser', user);
  },

  async getCurrentUser(): Promise<any | null> {
    try {
      const user = this.find('currentUser');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  async clearCurrentUser(): Promise<void> {
    this.delete('currentUser');
  },

  async isAuthenticated(): Promise<boolean> {
    return !!(await this.getCurrentUser());
  },

  // ============ REGISTRATION ============
  
  async registerUser(
    userData: any, 
    studentData?: Partial<any>, 
    schoolData?: Partial<any>
  ): Promise<{ user: any; student?: any; school?: any }> {
    // Check if user exists
    if (await this.getUserByEmail(userData.email)) {
      throw new Error('User with this email already exists');
    }

    // Create user
    const user = await this.createUser(userData);
    
    let student = null;
    let school = null;
    
    // If student, create student profile
    if (user.userType === 'student') {
      const defaultStudentData = {
        userId: user._id,
        email: user.email,
        firstName: user.name?.split(' ')[0] || '',
        lastName: user.name?.split(' ').slice(1).join(' ') || '',
        studentId: `STU${Date.now()}`,
        classLevel: 'JSS1',
        admissionDate: new Date(),
        status: 'active',
        schoolId: 'demo-school-id',
        preferences: {
          language: 'en',
          learningStyle: 'visual',
          notifications: {
            email: true,
            sms: false,
            push: true,
          },
          privacy: {
            shareWithParents: true,
            shareWithTeachers: true,
            sharePerformanceData: true,
          },
        },
        metadata: {
          onboardingCompleted: false,
          profileCompleteness: 0,
          riskFactors: [],
        },
        ...studentData,
      };
      
      student = await this.createStudent(defaultStudentData);
      
      // Link student to user
      await this.updateUser(user._id, { studentProfile: student._id });
    }
    
    // If school, create school profile
    if (user.userType === 'school') {
      const defaultSchoolData = {
        userId: user._id,
        email: user.email,
        name: user.name,
        schoolId: `SCH${Date.now()}`,
        address: '',
        phone: '',
        type: 'secondary',
        establishedYear: new Date().getFullYear(),
        status: 'active',
        ...schoolData,
      };
      
      school = await this.createSchool(defaultSchoolData);
      
      // Link school to user
      await this.updateUser(user._id, { schoolProfile: school._id });
    }

    // Auto-login after registration
    await this.setCurrentUser(user);
    
    return { user, student, school };
  },

  // ============ COMPATIBILITY METHODS ============
  
  async verifyPassword(email: string, password: string): Promise<boolean> {
    const user = await this.getUserByEmail(email);
    return !!(user && user.password === password);
  }
};